/**
 * ⚖️ B73-b (founder, 2026-08-29) — THE PROFILE CARD IS STILL EMPTY.
 *
 * B73 (from B53 PART B rec. 1) taught the Settings → Profile *Name* row to fall
 * back to `user_metadata.full_name` / `.name` when `athlete_context.Name` is
 * unset, so the card stops reading "Not set" while the sidebar prints the
 * athlete's name two inches away. That fixes every account whose metadata
 * HOLDS a name. It cannot fix the Apple account whose metadata never got one.
 *
 * Apple hands over `givenName` / `familyName` on the FIRST authorization for an
 * App ID and never again — it is not in the id_token, so Supabase never sees
 * it. `lib/appleAuth.ts` writes that one copy to disk before the exchange (the
 * 2026-08-03 incident) in a stash keyed by Apple's stable `sub`, and redeems it
 * on the next successful sign-in into two stores:
 *
 *   · `auth.users.user_metadata` (`updateUser`) — what B73's fallback reads;
 *   · `public.users.display_name` (`POST /api/user/profile-name`) — the roster.
 *
 * Neither of those is the field the Profile card's Name row EDITS. That is
 * `athlete_context.context_data.Name`, written by `POST /api/context/:userId`
 * (`lib/settingsPersist.ts` `saveField`) — and per the B53 store audit, NOTHING
 * writes it except a manual Settings edit. So an athlete whose metadata write
 * never landed (or landed on a session that has since been replaced) sees an
 * empty row for a name the device has had on disk the whole time.
 *
 * This module closes that one gap, and no more than that:
 *
 *   · it runs ONLY when the session's metadata carries NO name and the stash
 *     holds one for THIS EXACT Apple sub (the caller does the sub match — the
 *     stash lives in appleAuth and never leaves it);
 *   · it FILLS ONLY: an `athlete_context.Name` that already holds anything is
 *     left exactly as it is, because the athlete may have typed it;
 *   · it writes ONCE per athlete per device — {@link APPLE_NAME_SEED_KEY}
 *     records the seeded user id so a re-sign-in cannot loop;
 *   · it NEVER touches the sign-in identity. No `updateUser`, no session read,
 *     no token minting — a bearer in, one context read, one context write.
 *
 * B246 — the write is patch-only: `{ patch: { Name: full } }`. The server
 * always merges a body with no `context` key (B148 ruling), so this needs no
 * flag check and no wholesale fallback; nothing outside `Name` can be
 * clobbered. (Before B246 the write was deliberately both-shaped — `patch`
 * plus a read-then-merged whole `context` for a server with
 * `context_merge_writes` off — matching the B237 pattern that retired the
 * same dual shape from the other client writers.) A failed READ writes
 * nothing at all.
 *
 * Best-effort by construction: every outcome is a returned value, never a
 * throw, and the caller's stash lifetime is unaffected. A transient failure
 * here costs the context row's `Name` (the metadata + roster copies still
 * landed, and B73's fallback still renders the card) — never the sign-in.
 */

import { apiUrl } from './apiBase';

/** The seeded-once marker. Holds the user id it was written for, so a second
 *  athlete on the same device is not blocked by the first one's marker. */
export const APPLE_NAME_SEED_KEY = 'tayro_apple_name_seeded_v1';

export type SeedProfileNameOutcome =
  /** Written: the context row's `Name` was empty and now holds the stashed name. */
  | 'seeded'
  /** This athlete has already been seeded on this device. */
  | 'already-seeded'
  /** The context row already carries a `Name` — never overwritten. */
  | 'already-named'
  /** A precondition said no: metadata has a name, no stash, no session, no id. */
  | 'skipped'
  /** The read or the write failed. Nothing was written; nothing was marked. */
  | 'failed';

export interface SeedProfileNameArgs {
  /** Bearer for `/api/context/:userId`. Absent ⇒ nothing to write to. */
  token: string | null;
  /** The signed-in athlete's Supabase user id — the context row's key. */
  userId: string | null;
  /** The name from the sub-matched stash. '' when the stash is empty, belongs
   *  to a different Apple ID, or Apple simply never gave one. */
  stashedName: string;
  /** True when `user_metadata` already carries `full_name` / `name`. The whole
   *  point of this path is the account where it does not. */
  metadataHasName: boolean;
}

function alreadySeeded(userId: string): boolean {
  try {
    const raw = localStorage.getItem(APPLE_NAME_SEED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { userId?: string };
    return parsed?.userId === userId;
  } catch {
    return false;
  }
}

function markSeeded(userId: string): void {
  try {
    localStorage.setItem(APPLE_NAME_SEED_KEY, JSON.stringify({ userId }));
  } catch {
    /* storage unavailable — worst case we re-read the row next sign-in and
       find the name already there, which is the 'already-named' no-op. */
  }
}

/** Test-only: forget the once-marker so a case can run the path again. */
export function __resetAppleNameSeedForTest(): void {
  try {
    localStorage.removeItem(APPLE_NAME_SEED_KEY);
  } catch {
    /* nothing to do */
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A `Name` the athlete (or an earlier seed) already put there. */
function hasStoredName(context: Record<string, unknown>): boolean {
  const n = context.Name;
  return typeof n === 'string' && n.trim().length > 0;
}

/**
 * Fill `athlete_context.context_data.Name` from the Apple name stash — once,
 * fill-only, and only for a session whose metadata carries no name.
 *
 * See the module header for the full rationale. Never throws.
 */
export async function seedProfileDisplayName(
  args: SeedProfileNameArgs,
): Promise<SeedProfileNameOutcome> {
  const { token, userId, stashedName, metadataHasName } = args;

  // ── The three no-ops, in the order the brief states them ──────────────
  if (metadataHasName) return 'skipped'; // B73's fallback already renders a name.
  const full = stashedName.trim();
  if (!full) return 'skipped'; // No stash, or it belongs to another Apple ID.
  if (!token || !userId) return 'skipped'; // Nothing authenticated to write to.
  if (alreadySeeded(userId)) return 'already-seeded';

  try {
    const path = `/api/context/${encodeURIComponent(userId)}`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    // READ FIRST — to respect a name the athlete typed (the fill-only gate
    // below), not for a wholesale write: B246 made the write patch-only.
    const readRes = await fetch(apiUrl(path), { method: 'GET', headers });
    if (!readRes.ok) return 'failed';
    const body = (await readRes.json()) as { context?: unknown };
    const context = isPlainObject(body?.context) ? body.context : {};
    if (hasStoredName(context)) {
      // Nothing to do, ever again — the row has a real name.
      markSeeded(userId);
      return 'already-named';
    }

    // B246 — patch-only. The server always merges a body with no `context`
    // key (B148 ruling), so only the key this seed sets is sent.
    const writeRes = await fetch(apiUrl(path), {
      method: 'POST',
      headers,
      body: JSON.stringify({ patch: { Name: full } }),
    });
    if (!writeRes.ok) return 'failed';

    markSeeded(userId);
    return 'seeded';
  } catch {
    return 'failed';
  }
}
